<template>
  <header class="company-header">
    <div class="company-header__surface">
      <div class="company-header__identity">
        <div class="company-header__avatar">
          <i class="fa fa-building"></i>
        </div>

        <div class="company-header__copy">
          <p class="company-header__eyebrow">Cliente corporativo</p>

          <div class="company-header__title-row">
            <h1 class="company-header__title">
              {{ company.name || 'Sin nombre' }}
            </h1>

            <button
              v-if="canEditCompany"
              class="company-header__icon-action"
              type="button"
              @click="$emit('edit-record')"
            >
              <i class="fa fa-pen"></i>
            </button>
          </div>

          <div class="company-header__subtitle-row">
            <p class="company-header__subtitle">
              {{ fullRut || 'Sin información tributaria' }}
            </p>

            <button
              v-if="normalizedRut"
              class="company-header__copy-action"
              type="button"
              :title="copyRutLabel"
              :aria-label="copyRutLabel"
              @click="copyRut"
            >
              <i class="fa" :class="copyRutIcon"></i>
              <span>{{ copyRutLabel }}</span>
            </button>
          </div>

          <p v-if="!showLegalFiscalNotApplicableNotice" class="company-header__supporting-meta">
            Inicio de actividades: <strong>{{ formattedStartActivities }}</strong>
          </p>

          <p v-if="showLegalFiscalNotApplicableNotice" class="company-header__supporting-note">
            Los atributos legales y fiscales quedaron fuera de la ficha completa.
            La información histórica se mantiene solo como referencia en la pestaña Legal y Fiscal.
          </p>

          <div v-if="!showLegalFiscalNotApplicableNotice" class="company-header__meta-grid">
            <div class="company-header__meta-item">
              <span class="company-header__meta-label">Régimen</span>
              <strong class="company-header__meta-value">{{ record.tax_regime_name || 'Sin información' }}</strong>
            </div>

            <div class="company-header__meta-item">
              <span class="company-header__meta-label">Facturador</span>
              <strong class="company-header__meta-value">{{ record.biller_type_name || 'Sin información' }}</strong>
            </div>

            <div class="company-header__meta-item">
              <span class="company-header__meta-label">Tipo</span>
              <strong class="company-header__meta-value">{{ record.organizational_type || 'Sin información' }}</strong>
            </div>
          </div>
        </div>
      </div>

      <div class="company-header__aside">
        <div class="company-header__status-block">
          <p class="company-header__status-label">Estado</p>

          <button
            class="company-header__status"
            :class="statusClass"
            type="button"
            :disabled="!canChangeStatus"
            @click="$emit('change-status')"
          >
            {{ company.status || 'Activo' }}
          </button>
        </div>

        <div class="company-header__completion">
          <div class="company-header__completion-top">
            <span>Ficha</span>
            <strong>{{ completion.percentage }}%</strong>
          </div>

          <div class="company-header__completion-bar">
            <span
              class="company-header__completion-fill"
              :style="{ width: `${completion.percentage}%` }"
            ></span>
          </div>
        </div>

        <div class="company-header__actions">
          <button class="company-header__primary" type="button" @click="$emit('edit-record')">
            Editar ficha
          </button>

          <button class="company-header__secondary" type="button" @click="$emit('add-related')">
            Agregar relación
          </button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { formatDateOnly } from '@/utils/date'

export default {
  name: 'CompanyWorkspaceHeader',
  props: {
    company: {
      type: Object,
      required: true
    },
    record: {
      type: Object,
      required: true
    },
    completion: {
      type: Object,
      required: true
    },
    canEditCompany: {
      type: Boolean,
      default: false
    },
    canChangeStatus: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      copyRutState: 'idle',
      copyRutResetTimeout: null
    }
  },
  computed: {
    fullRut () {
      if (!this.company.rut) return ''
      const value = this.company.rut.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      return `${value}-${this.company.dv || ''}`
    },
    normalizedRut () {
      const rut = this.company && this.company.rut ? this.company.rut.toString().replace(/\D/g, '') : ''
      const dv = this.company && this.company.dv ? this.company.dv.toString().replace(/[^0-9kK]/g, '') : ''
      return `${rut}${dv}` || ''
    },
    copyRutLabel () {
      if (this.copyRutState === 'success') return 'Copiado'
      if (this.copyRutState === 'error') return 'No se pudo copiar'
      return 'Copiar RUT'
    },
    copyRutIcon () {
      if (this.copyRutState === 'success') return 'fa-check'
      if (this.copyRutState === 'error') return 'fa-exclamation-circle'
      return 'fa-copy'
    },
    formattedStartActivities () {
      return formatDateOnly(this.record.start_activities)
    },
    showLegalFiscalNotApplicableNotice () {
      return this.company && this.company.client_type === 'persona_natural' && this.record && this.record.requires_legal_fiscal_info === false
    },
    statusClass () {
      const status = (this.company.status || 'Activo').toLowerCase()
      if (status === 'inactivo') return 'company-header__status--inactive'
      if (status.includes('ttg')) return 'company-header__status--warning'
      if (status === 'esporádico') return 'company-header__status--muted'
      return 'company-header__status--active'
    }
  },
  beforeDestroy () {
    this.clearCopyRutResetTimeout()
  },
  methods: {
    clearCopyRutResetTimeout () {
      if (this.copyRutResetTimeout) {
        clearTimeout(this.copyRutResetTimeout)
        this.copyRutResetTimeout = null
      }
    },
    scheduleCopyRutReset () {
      this.clearCopyRutResetTimeout()
      this.copyRutResetTimeout = setTimeout(() => {
        this.copyRutState = 'idle'
        this.copyRutResetTimeout = null
      }, 1800)
    },
    async copyRut () {
      if (!this.normalizedRut || typeof navigator === 'undefined' || !navigator.clipboard) {
        this.copyRutState = 'error'
        this.scheduleCopyRutReset()
        return
      }

      try {
        await navigator.clipboard.writeText(this.normalizedRut)
        this.copyRutState = 'success'
      } catch (error) {
        this.copyRutState = 'error'
      }

      this.scheduleCopyRutReset()
    }
  }
}
</script>

<style scoped>
.company-header {
  margin-bottom: 0.9rem;
}

.company-header__surface {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(300px, 0.85fr);
  gap: 1rem;
  padding: 1.1rem 1.2rem;
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: 30px;
  background:
    radial-gradient(circle at top right, rgba(235, 222, 204, 0.72), transparent 30%),
    linear-gradient(180deg, rgba(255, 252, 247, 0.98), rgba(245, 238, 229, 0.9));
  box-shadow: 0 18px 42px rgba(40, 29, 18, 0.05);
}

.company-header__identity {
  display: flex;
  gap: 0.95rem;
  min-width: 0;
}

.company-header__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.74);
  color: #34291f;
  font-size: 1.28rem;
  flex-shrink: 0;
}

.company-header__copy {
  min-width: 0;
}

.company-header__eyebrow {
  margin: 0 0 0.35rem;
  font-size: 0.68rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(53, 42, 30, 0.5);
}

.company-header__title-row {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
}

.company-header__title {
  margin: 0;
  font-size: clamp(1.85rem, 2.75vw, 2.55rem);
  line-height: 0.96;
  letter-spacing: -0.05em;
  color: #201811;
}

.company-header__icon-action {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.62);
  color: #372c22;
  font-size: 0.82rem;
}

.company-header__subtitle {
  margin: 0;
  font-size: 0.98rem;
  color: rgba(33, 25, 18, 0.64);
}

.company-header__subtitle-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.42rem;
}

.company-header__copy-action {
  display: inline-flex;
  align-items: center;
  gap: 0.38rem;
  min-height: 1.9rem;
  padding: 0.32rem 0.7rem;
  border: 1px solid rgba(104, 83, 57, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.68);
  color: #352b21;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.company-header__supporting-meta {
  margin: 0.6rem 0 0;
  color: rgba(53, 42, 30, 0.62);
  font-size: 0.9rem;
}

.company-header__supporting-meta strong {
  color: #30251d;
  font-weight: 700;
}

.company-header__supporting-note {
  display: inline-block;
  margin: 0.55rem 0 0;
  padding: 0.48rem 0.78rem;
  max-width: 42rem;
  border-radius: 18px;
  background: rgba(184, 139, 59, 0.14);
  color: #7a5925;
  font-size: 0.8rem;
  font-weight: 700;
  line-height: 1.45;
}

.company-header__meta-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 0.78rem;
}

.company-header__meta-item {
  display: grid;
  gap: 0.12rem;
  min-width: 132px;
  padding: 0.64rem 0.82rem;
  border: 1px solid rgba(104, 83, 57, 0.08);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.48);
}

.company-header__meta-label {
  color: rgba(58, 46, 33, 0.48);
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.company-header__meta-value {
  color: #2e241b;
  font-size: 0.92rem;
  font-weight: 700;
  line-height: 1.2;
}

.company-header__aside {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.company-header__status-label {
  margin: 0 0 0.3rem;
  font-size: 0.7rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(53, 42, 30, 0.5);
}

.company-header__status {
  min-height: 2.15rem;
  padding: 0.42rem 0.85rem;
  border: 0;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.company-header__status--active {
  background: rgba(77, 140, 97, 0.14);
  color: #2f6a43;
}

.company-header__status--inactive {
  background: rgba(168, 78, 78, 0.12);
  color: #8a3f3f;
}

.company-header__status--warning {
  background: rgba(184, 139, 59, 0.14);
  color: #8a6520;
}

.company-header__status--muted {
  background: rgba(95, 107, 122, 0.14);
  color: #5a6574;
}

.company-header__completion-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.32rem;
  color: #34291f;
  font-size: 0.82rem;
}

.company-header__completion-bar {
  height: 10px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(90, 72, 48, 0.09);
}

.company-header__completion-fill {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2c241b 0%, #93724a 100%);
}

.company-header__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.company-header__primary,
.company-header__secondary {
  min-height: 2.55rem;
  padding: 0 0.9rem;
  border-radius: 999px;
}

.company-header__primary {
  border: 0;
  background: linear-gradient(135deg, #241c15 0%, #4b3a29 100%);
  color: #f7f0e5;
}

.company-header__secondary {
  border: 1px solid rgba(104, 83, 57, 0.14);
  background: rgba(255, 255, 255, 0.62);
  color: #352b21;
}

@media (min-width: 1600px) and (max-width: 1799px) {
  .company-header__surface {
    grid-template-columns: minmax(0, 1.55fr) minmax(320px, 0.86fr);
    padding: 1.04rem 1.12rem;
  }

  .company-header__title {
    font-size: clamp(1.82rem, 2.3vw, 2.45rem);
  }
}

@media (min-width: 1800px) {
  .company-header__surface {
    grid-template-columns: minmax(0, 1.6fr) minmax(320px, 0.82fr);
    padding: 1rem 1.1rem;
  }

  .company-header__title {
    font-size: clamp(1.75rem, 2.45vw, 2.35rem);
  }

  .company-header__meta-grid {
    gap: 0.5rem;
  }

  .company-header__meta-item {
    padding: 0.58rem 0.78rem;
  }
}

@media (max-width: 980px) {
  .company-header__surface {
    grid-template-columns: 1fr;
  }

  .company-header__aside {
    align-items: flex-start;
  }
}

@media (max-width: 640px) {
  .company-header__surface {
    padding: 1rem;
    border-radius: 24px;
  }

  .company-header__meta-grid {
    display: grid;
    grid-template-columns: 1fr;
  }

  .company-header__meta-item {
    min-width: 0;
  }

  .company-header__title-row,
  .company-header__identity {
    align-items: flex-start;
  }

  .company-header__title-row {
    flex-wrap: wrap;
  }

  .company-header__actions {
    flex-direction: column;
  }

  .company-header__primary,
  .company-header__secondary,
  .company-header__status {
    width: 100%;
    justify-content: center;
  }
}
</style>


